import logger from '/imports/startup/client/logger';

// Packet loss thresholds and durations
const LOSS_WARN_THRESHOLD = 0.05;   // 5%
const LOSS_WARN_DURATION_MS = 15000; // 15 seconds
const LOSS_CRITICAL_THRESHOLD = 0.10; // 10%
const LOSS_CRITICAL_DURATION_MS = 5000; // 5 seconds
const STATS_POLL_INTERVAL_MS = 2000;
// Require this many consecutive threshold breaches before killing.
// Prevents a single transient spike from tearing down the stream.
const MIN_CONSECUTIVE_BREACHES = 2;

class ScreenshareQualityMonitor {
  constructor(peerConnection, onKill) {
    this._pc = peerConnection;
    this._onKill = onKill;
    this._interval = null;
    this._prevStats = null;
    this._warningSince = null;   // timestamp when loss first exceeded WARN threshold
    this._criticalSince = null;  // timestamp when loss first exceeded CRITICAL threshold
    this._consecutiveCritical = 0; // consecutive polls above critical threshold
    this._consecutiveWarn = 0;     // consecutive polls above warning threshold
  }

  start() {
    if (this._interval) return;
    this._interval = setInterval(() => this._poll(), STATS_POLL_INTERVAL_MS);
  }

  stop() {
    if (this._interval) {
      clearInterval(this._interval);
      this._interval = null;
    }
    this._prevStats = null;
    this._warningSince = null;
    this._criticalSince = null;
    this._consecutiveCritical = 0;
    this._consecutiveWarn = 0;
  }

  async _poll() {
    if (!this._pc || this._pc.connectionState === 'closed') {
      this.stop();
      return;
    }

    try {
      const stats = await this._pc.getStats();
      let totalPacketsReceived = 0;
      let totalPacketsLost = 0;

      stats.forEach((report) => {
        if (report.type === 'inbound-rtp' && report.kind === 'video') {
          totalPacketsReceived += report.packetsReceived || 0;
          totalPacketsLost += report.packetsLost || 0;
        }
      });

      if (this._prevStats) {
        const deltaReceived = totalPacketsReceived - this._prevStats.packetsReceived;
        const deltaLost = totalPacketsLost - this._prevStats.packetsLost;
        const deltaTotal = deltaReceived + deltaLost;

        if (deltaTotal > 0) {
          const lossRatio = deltaLost / deltaTotal;
          this._evaluate(lossRatio);
        } else {
          // No packets in this interval — reset timers
          this._warningSince = null;
          this._criticalSince = null;
          this._consecutiveCritical = 0;
          this._consecutiveWarn = 0;
        }
      }

      this._prevStats = {
        packetsReceived: totalPacketsReceived,
        packetsLost: totalPacketsLost,
      };
    } catch (error) {
      logger.debug({
        logCode: 'screenshare_quality_monitor_stats_error',
        extraInfo: { errorMessage: error.message },
      }, 'Quality monitor failed to get stats');
    }
  }

  _evaluate(lossRatio) {
    const now = Date.now();

    // Critical threshold check (10% for 5s, with consecutive breach requirement)
    if (lossRatio >= LOSS_CRITICAL_THRESHOLD) {
      this._consecutiveCritical += 1;
      if (!this._criticalSince) {
        this._criticalSince = now;
      } else if (
        now - this._criticalSince >= LOSS_CRITICAL_DURATION_MS
        && this._consecutiveCritical >= MIN_CONSECUTIVE_BREACHES
      ) {
        logger.error({
          logCode: 'screenshare_quality_critical',
          extraInfo: {
            lossRatio,
            durationMs: now - this._criticalSince,
            consecutiveBreaches: this._consecutiveCritical,
          },
        }, `Screenshare packet loss critical (${(lossRatio * 100).toFixed(1)}%) — stopping`);
        this._kill();
        return;
      }
    } else {
      this._criticalSince = null;
      this._consecutiveCritical = 0;
    }

    // Warning threshold check (5% for 15s, with consecutive breach requirement)
    if (lossRatio >= LOSS_WARN_THRESHOLD) {
      this._consecutiveWarn += 1;
      if (!this._warningSince) {
        this._warningSince = now;
      } else if (
        now - this._warningSince >= LOSS_WARN_DURATION_MS
        && this._consecutiveWarn >= MIN_CONSECUTIVE_BREACHES
      ) {
        logger.warn({
          logCode: 'screenshare_quality_warning',
          extraInfo: {
            lossRatio,
            durationMs: now - this._warningSince,
            consecutiveBreaches: this._consecutiveWarn,
          },
        }, `Screenshare packet loss sustained (${(lossRatio * 100).toFixed(1)}%) — stopping`);
        this._kill();
        return;
      }
    } else {
      this._warningSince = null;
      this._consecutiveWarn = 0;
    }
  }

  _kill() {
    this.stop();
    if (typeof this._onKill === 'function') {
      this._onKill();
    }
  }
}

export default ScreenshareQualityMonitor;
